import { brandStorage as localStorage } from '@/utils/brandSession';
import React, { useRef, useState } from 'react';
import axios from 'axios';
import { Download, FileArchive, FileImage, FileSpreadsheet, FileText, Loader2, Pencil, Presentation, Trash2, Upload, X, Check } from 'lucide-react';
import { API } from '../../utils/config';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { toast } from 'sonner';

const ACCEPTED = '.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.png,.jpg,.jpeg,.webp,.zip';

const formatBytes = (bytes = 0) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const AttachmentIcon = ({ name }) => {
  const ext = (name || '').split('.').pop()?.toLowerCase();
  if (ext === 'zip') return <FileArchive className="h-5 w-5 text-amber-600" />;
  if (['png', 'jpg', 'jpeg', 'webp'].includes(ext)) return <FileImage className="h-5 w-5 text-cyan-700" />;
  if (['xls', 'xlsx'].includes(ext)) return <FileSpreadsheet className="h-5 w-5 text-emerald-700" />;
  if (['ppt', 'pptx'].includes(ext)) return <Presentation className="h-5 w-5 text-orange-700" />;
  return <FileText className="h-5 w-5 text-blue-700" />;
};

export const TrainingAttachments = ({ video, admin = false, onChanged }) => {
  const attachments = video?.attachments || [];
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [renamingId, setRenamingId] = useState(null);
  const [renameValue, setRenameValue] = useState('');

  const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('hw_token')}` });

  const uploadFiles = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    const oversized = files.find(file => file.size > 100 * 1024 * 1024);
    if (oversized) {
      toast.error(`${oversized.name}: dimensione massima 100 MB`);
      return;
    }
    setUploading(true);
    setProgress(0);
    try {
      for (let index = 0; index < files.length; index += 1) {
        const form = new FormData();
        form.append('file', files[index]);
        await axios.post(`${API}/api/training/videos/${video.id}/attachments`, form, {
          headers: { ...authHeaders(), 'Content-Type': 'multipart/form-data' },
          timeout: 180000,
          onUploadProgress: event => {
            if (!event.total) return;
            const current = event.loaded / event.total;
            setProgress(Math.round(((index + current) / files.length) * 100));
          },
        });
      }
      toast.success(files.length === 1 ? 'Allegato caricato' : `${files.length} allegati caricati`);
      if (fileInputRef.current) fileInputRef.current.value = '';
      await onChanged?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Caricamento allegato non riuscito');
    } finally {
      setUploading(false);
      setProgress(0);
    }
  };

  const download = async (attachment) => {
    try {
      const response = await axios.post(
        `${API}/api/training/videos/${video.id}/attachments/${attachment.id}/download`,
        {},
        { headers: authHeaders() }
      );
      window.location.assign(response.data.url);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Download non disponibile');
    }
  };

  const rename = async (attachment) => {
    try {
      await axios.patch(
        `${API}/api/training/videos/${video.id}/attachments/${attachment.id}`,
        { display_name: renameValue },
        { headers: authHeaders() }
      );
      toast.success('Nome aggiornato');
      setRenamingId(null);
      await onChanged?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Rinomina non riuscita');
    }
  };

  const remove = async (attachment) => {
    if (!window.confirm(`Eliminare l'allegato “${attachment.display_name}”?`)) return;
    try {
      await axios.delete(`${API}/api/training/videos/${video.id}/attachments/${attachment.id}`, { headers: authHeaders() });
      toast.success('Allegato eliminato');
      await onChanged?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Eliminazione non riuscita');
    }
  };

  if (!admin && attachments.length === 0) return null;

  return (
    <section className="mt-4 border-t border-slate-200 pt-4" data-testid={`training-attachments-${video.id}`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h3 className="text-sm font-bold text-slate-800">Materiali allegati</h3>
          <p className="text-xs text-slate-500">{attachments.length ? `${attachments.length} file disponibili` : 'Nessun allegato'}</p>
        </div>
        {admin && (
          <div className="w-full sm:w-auto">
            <Input
              ref={fileInputRef}
              type="file"
              multiple
              accept={ACCEPTED}
              onChange={event => uploadFiles(event.target.files)}
              disabled={uploading}
              className="hidden"
              data-testid={`training-attachment-file-${video.id}`}
            />
            <Button size="sm" variant="outline" onClick={() => fileInputRef.current?.click()} disabled={uploading} className="w-full sm:w-auto" data-testid={`training-attachment-upload-${video.id}`}>
              {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
              <span className="ml-1">Allega file</span>
            </Button>
          </div>
        )}
      </div>
      {uploading && (
        <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-slate-200" data-testid={`training-attachment-progress-${video.id}`}>
          <div className="h-full bg-blue-600 transition-[width]" style={{ width: `${progress}%` }} />
        </div>
      )}
      {attachments.length > 0 && (
        <div className="mt-3 divide-y divide-slate-100 border-y border-slate-100">
          {attachments.map(attachment => (
            <div key={attachment.id} className="grid grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-2 py-2" data-testid={`training-attachment-${attachment.id}`}>
              <AttachmentIcon name={attachment.display_name} />
              <div className="min-w-0 flex-1">
                {renamingId === attachment.id ? (
                  <Input value={renameValue} onChange={event => setRenameValue(event.target.value)} className="h-8" data-testid={`training-attachment-rename-input-${attachment.id}`} />
                ) : (
                  <p className="truncate text-sm font-medium text-slate-800" data-testid={`training-attachment-name-${attachment.id}`}>{attachment.display_name}</p>
                )}
                <p className="text-xs text-slate-400" data-testid={`training-attachment-size-${attachment.id}`}>{formatBytes(attachment.size)}</p>
              </div>
              <div className="flex items-center gap-1">
                <Button size="sm" variant="ghost" onClick={() => download(attachment)} title="Scarica" data-testid={`training-attachment-download-${attachment.id}`}>
                  <Download className="h-4 w-4" />
                </Button>
                {admin && renamingId === attachment.id && (
                  <>
                    <Button size="sm" variant="ghost" onClick={() => rename(attachment)} title="Salva nome" data-testid={`training-attachment-rename-save-${attachment.id}`}><Check className="h-4 w-4" /></Button>
                    <Button size="sm" variant="ghost" onClick={() => setRenamingId(null)} title="Annulla" data-testid={`training-attachment-rename-cancel-${attachment.id}`}><X className="h-4 w-4" /></Button>
                  </>
                )}
                {admin && renamingId !== attachment.id && (
                  <>
                    <Button size="sm" variant="ghost" onClick={() => { setRenamingId(attachment.id); setRenameValue(attachment.display_name); }} title="Rinomina" data-testid={`training-attachment-rename-${attachment.id}`}><Pencil className="h-4 w-4" /></Button>
                    <Button size="sm" variant="ghost" onClick={() => remove(attachment)} className="text-red-600 hover:bg-red-50" title="Elimina" data-testid={`training-attachment-delete-${attachment.id}`}><Trash2 className="h-4 w-4" /></Button>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
      {admin && <p className="mt-2 text-xs text-slate-400">PDF, Word, Excel, PowerPoint, immagini o ZIP · massimo 100 MB</p>}
    </section>
  );
};